import { Link } from 'react-router-dom'
import type { Project } from '@/types/content'
import { ROUTES } from '@/lib/paths'
import { Icon, Surface } from '@/components/ui'
import { ProjectCard } from './ProjectCard'
import styles from './ProjectNav.module.css'

interface ProjectNavProps {
  previous?: Project | undefined
  next?: Project | undefined
  related: Project[]
}

/**
 * Where to go after a case study.
 *
 * Previous/next let a visitor walk the whole portfolio without returning to
 * the index each time — the thing that made the old detail page a dead end.
 */
export const ProjectNav = ({ previous, next, related }: ProjectNavProps) => (
  <div className={styles.wrap}>
    <nav className={styles.pager} aria-label="Project navigation">
      {previous ? (
        <Surface
          as={Link}
          to={ROUTES.project(previous.slug)}
          interactive
          radius="card"
          className={styles.step}
        >
          <span className={`t-12 ${styles.direction}`}>
            <Icon name="arrowLeft" size={13} />
            Previous
          </span>
          <span className={`t-18 ${styles.stepTitle}`}>{previous.title}</span>
        </Surface>
      ) : (
        <span />
      )}

      {next && (
        <Surface
          as={Link}
          to={ROUTES.project(next.slug)}
          interactive
          radius="card"
          className={`${styles.step} ${styles.stepNext}`}
        >
          <span className={`t-12 ${styles.direction}`}>
            Next
            <Icon name="arrowRight" size={13} />
          </span>
          <span className={`t-18 ${styles.stepTitle}`}>{next.title}</span>
        </Surface>
      )}
    </nav>

    {related.length > 0 && (
      <div className={styles.related}>
        <h2 className="t-20">More work</h2>
        <ul className={styles.grid}>
          {related.map((project) => (
            <li key={project.slug}>
              <ProjectCard project={project} size="small" />
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
)
